import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    Link,
    Box,
    IconButton
} from "@mui/material";
import { DataGridPro, GridColDef, GridFooterContainer, GridPagination } from "@mui/x-data-grid-pro";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import { FileRejection, useDropzone } from "react-dropzone";
import useAuth, { authData } from "../context/AuthProvider";
import { Form, Formik } from "formik";
import Swal from "sweetalert2";
import { apiClient } from "../helper/api";
import { UploadedDocumentType } from "../models/TaxTransactionReportModel";
import Tooltip from '@mui/material/Tooltip';
import moment from "moment";
import { CustomerPolicyDetailsModel } from "../models/CustomerPolicyDetailsModel";

interface props {
    open: boolean;
    handleClose: () => void;
    customerPolicyId: any;
    currentUser?: authData;
    viewOnly?: boolean;
}

interface RequiredDocument {
    documentTypeId: number;
    documentName: string;
    isRequired: boolean;
}

export const AddDocument: React.FC<props> = ({ open, handleClose, customerPolicyId, currentUser, viewOnly = false }) => {
    const initialValues: {} = {};
    const [loading, setLoading] = useState(false);
    const [policyDetail, setPolicyDetail] = useState<CustomerPolicyDetailsModel>()
    const [requiredDocument, setRequiredDocument] = useState<RequiredDocument[]>([])
    const [docTypeIds, setSelectedDocTypeIds] = useState<number[]>([])
    const { user } = useAuth();
    const token = user?.token;
    const [UploadedDocument, setUploadedDocument] = React.useState<UploadedDocumentType[]>([]);
    const [revertedDocument, setRevertedDocument] = React.useState<UploadedDocumentType[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<File | null>(null);
    const uploadAreaRef = useRef<HTMLDivElement>(null);

    const hasUploadedDocs = UploadedDocument.some((doc: UploadedDocumentType) => doc.documentTypeId !== 0);
    const hasRevertedDocs = revertedDocument.some((doc: UploadedDocumentType) => doc.documentTypeId !== 0);

    // Helper function to determine if user can delete documents (admin or client users)
    // Also returns false if viewOnly mode is enabled
    const canDeleteDocuments = () => {
        if (viewOnly) return false;
        if (!user?.userRoles) return false;
        const roles = user.userRoles.split(",");
        return roles.some(role => 
            ['Super Admin', 'Client Admin', 'Client Accounting', 'Client Operations', 'Client Processors'].includes(role.trim())
        );
    };

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    useEffect(() => {
        if (open) {
            fetchPolicyDetail();
            getUploadedDocuments();
            getRevertedDocuments();
            getRequiredDocuments();
        }
    }, [open]);

    useEffect(() => {
        if (
            open &&
            uploadAreaRef.current &&
            (requiredDocument.length > 0 || UploadedDocument.length > 0 || revertedDocument.length > 0)
        ) {
            uploadAreaRef.current.scrollIntoView({ behavior: "auto", block: "start" });
        }
    }, [open, requiredDocument, UploadedDocument, revertedDocument]);

    const fetchPolicyDetail = () => {
        apiClient(`/PolicyDashBoard/getCustomerPolicyDetails?customerPolicyId=${customerPolicyId}`,{
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((res) => {
            if (!res) return;
            setPolicyDetail(res?.data);
        });
    }

    const getRequiredDocuments = () => {
        return apiClient(`/customer/getRequiredDocuments?customerPolicyId=${customerPolicyId}&all=0`, {
          method: "GET",
          headers: new Headers({
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
          }),
        })
        .then(responseJson => {
            if (responseJson.data && responseJson.data.length > 0) {
              setRequiredDocument(responseJson.data);
              setLoading(false);
            } else {
                setRequiredDocument([]);
                setSelectedDocTypeIds([]);
                setLoading(false);
            }
        })
        .catch(error => {
          setSelectedDocTypeIds([]);
          setRequiredDocument([]);
          setLoading(false);
        });
    };

    const getUploadedDocuments = () => {
        setLoading(true)
        return apiClient(`/Customer/getUploadedDocuments?customerPolicyId=${customerPolicyId}`,
        {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((res) => {
            if (!res || !res.data || !Array.isArray(res.data)) {
                setUploadedDocument([]);
                setLoading(false);
                return;
            }
            setUploadedDocument(res.data.filter((item: UploadedDocumentType) => item.documentTypeId != 0));
            setLoading(false)
        }).catch((error) => {
            setUploadedDocument([]);
            setLoading(false);
        })
    }

    const getRevertedDocuments = () => {
        setLoading(true)
        return apiClient(`/customer/getRevertedDocuments?customerPolicyId=${customerPolicyId}`,
        {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((res) => {
            if (!res || !res.data || !Array.isArray(res.data)) {
                setRevertedDocument([]);
                setLoading(false);
                return;
            }
            setRevertedDocument(res.data.filter((item: UploadedDocumentType) => item.documentTypeId != 0));
            setLoading(false)
        }).catch((error) => {
            setRevertedDocument([]);
            setLoading(false);
        })
    }

    const handleFileDrop = (file: File, srdmId: number) => {
        setSelectedFiles(file);
    };

    const handleFileRemove = () => {
        setSelectedDocTypeIds([]);
        setSelectedFiles(null);
    };

    const handleBoxClose = () => {
        setSelectedFiles(null);
        setRequiredDocument([])
        setSelectedDocTypeIds([]);
        handleClose();
        setUploadedDocument([]);
        setRevertedDocument([]);
    }

    const SentToMisc = () => {
        if (!hasUploadedDocs && !hasRevertedDocs) {
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Please upload at least one document before sending to MISC',
                confirmButtonText: 'OK'
            });
            return;
        }
        Swal.fire({
            title: 'Send to MISC',
            text: 'Are you sure you want to send this transaction to MISC?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#007BFF',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            padding: '20px',
            backdrop: true,
            customClass: {
                popup: 'custom-swal-popup',
                confirmButton: 'btn-radius',
                cancelButton: 'btn-radius',
            },
            width: '500px',
        })
        .then(async (result: { isConfirmed: any; }) => {
        if (result.isConfirmed) {
            try {
                // If there are reverted documents, convert them to uploaded documents first
                if (hasRevertedDocs) {
                    await apiClient(`/customer/convertRevertedToUploaded?customerPolicyId=${customerPolicyId}`, {
                        method: "GET",
                        headers: new Headers({
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        })
                    });
                }

                // Now send to MISC
                const res = await apiClient(`/customer/SentToMisc?customerPolicyId=${customerPolicyId}`, {
                    method: "GET",
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    })
                });

                if (!res) return;
                if (res.data != null) {
                    Swal.fire({
                        title: 'Success!',
                        text: 'The transaction has been successfully sent to MISC.',
                        icon: 'success',
                        confirmButtonColor: '#007BFF',
                        confirmButtonText: 'OK',
                        padding: '20px',
                        customClass: {
                            popup: 'custom-swal-popup',
                            confirmButton: 'btn-radius',
                        },
                        width: '400px',
                    }).then((result) => {
                        handleBoxClose();
                    });
                }
            } catch (error) {
                console.error('Error sending to MISC:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'An error occurred while sending to MISC. Please try again.',
                    confirmButtonText: 'OK'
                });
            }
        } else {
            console.warn("User cancelled document revert.");
        }
        })
    }

    const handleCheckboxChange = (item: RequiredDocument) => {
        const documentTypeId = item?.documentTypeId;
        if (!documentTypeId) return;
        setSelectedDocTypeIds((prevDocTypeIds) => {
            if (prevDocTypeIds.includes(documentTypeId)) {
                return prevDocTypeIds.filter((id) => id !== documentTypeId);
            } else {
                return [...prevDocTypeIds, documentTypeId];
            }
        });
    };

    const handleRemoveUploadedDoc = async (rowData: any) => {

        const resDlg = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            iconHtml: '<svg width="70" height="70" viewBox="0 0 62 62" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="31" cy="31" r="31" fill="#02A4E3"/><path d="M31.0003 25.6667V31M31.0003 36.3334H31.0137M44.3337 31C44.3337 38.3638 38.3641 44.3334 31.0003 44.3334C23.6365 44.3334 17.667 38.3638 17.667 31C17.667 23.6362 23.6365 17.6667 31.0003 17.6667C38.3641 17.6667 44.3337 23.6362 44.3337 31Z" stroke="white" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
            showCancelButton: true,
            confirmButtonColor: '#02A4E3',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Delete',
            customClass: {
                confirmButton: 'btn-radius',
                cancelButton: 'btn-radius',
                popup: 'card-radius'
            }
        })

        if (!resDlg.isConfirmed) {
            return;
        }

        setLoading(true);
        try {
            // Delete all documents in this row
            const deletePromises = rowData.allDocuments.map((doc: UploadedDocumentType) => 
                apiClient(`/customer/DeleteUploadedDocument?customerPolicyDocumentId=${doc.customerPolicyDocumentId}`, {
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            method: "Delete",
        })
            );

            await Promise.all(deletePromises);

            // Refresh data after successful deletion
            await Promise.all([
                getUploadedDocuments(),
                getRevertedDocuments(),
                getRequiredDocuments()
            ]);

            Swal.fire({
                icon: 'success',
                title: 'Deleted Successfully!',
                text: `The file "${rowData.fileName}" and its document types have been successfully deleted.`,
                confirmButtonText: 'OK'
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Delete Failed',
                text: 'Some documents could not be deleted. Please try again.',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    }

    const getDocTypeName = (docTypeId: number) => {
        const doc = requiredDocument.find((d) => d.documentTypeId === docTypeId);
        return doc ? doc.documentName : "";
    }

    // Helper function to check if user is a client user (not admin)
    const isClientUser = () => {
        if (!user?.userRoles) return false;
        const roles = user.userRoles.split(",");
        return roles.some(role => 
            ['Customer User', 'Client Admin', 'Client Accounting', 'Client Operations', 'Client Processors'].includes(role.trim())
        );
    };

    // Helper function to display MISC user names as "miscstaff" for client users
    const getDisplayName = (uploadedBy: string, uploadedByRoleId?: number) => {
        if (!user?.userRoles) return uploadedBy || '-';
        
        if (isClientUser()) {
            const clientRoleIds = [8, 9, 10, 11];
            return clientRoleIds.includes(uploadedByRoleId || 0) ? uploadedBy || '-' : 'miscstaff';
        } else {
            return uploadedBy || '-';
        }
    }

    const groupedRows = useMemo(() => {
        // Group uploaded documents
        const uploadGroups = new Map<string, UploadedDocumentType[]>();
        UploadedDocument.forEach((doc) => {
            const fileName = Array.isArray((doc as any).documentName) ? ((doc as any).documentName?.[0] || '') : (doc as any).documentName;
            const key = `${doc.blob_Address}::${fileName}`;
            const arr = uploadGroups.get(key) || [];
            arr.push(doc);
            uploadGroups.set(key, arr);
        });

        // Group reverted documents
        const revertGroups = new Map<string, UploadedDocumentType[]>();
        revertedDocument.forEach((doc) => {
            const fileName = Array.isArray((doc as any).documentName) ? ((doc as any).documentName?.[0] || '') : (doc as any).documentName;
            const key = `${doc.blob_Address}::${fileName}`;
            const arr = revertGroups.get(key) || [];
            arr.push(doc);
            revertGroups.set(key, arr);
        });

        // Process uploaded documents (latest first)
        const uploadedRows = Array.from(uploadGroups.values()).map((docs, idx) => {
            const first = docs[0] as any;
            const fileName = Array.isArray(first.documentName) ? (first.documentName?.[0] || '') : first.documentName;
            const docTypeNames = Array.from(new Set(
                docs.map((d) => getDocTypeName(d.documentTypeId) || (d as any).name).filter(Boolean)
            )).join(', ');
            const uploadedAt = first.dateUploadedAt ? moment.utc(first.dateUploadedAt).local().format('MM/DD/YYYY HH:mm') : '-';
            const uploadedBy = getDisplayName(first.uploadedByFullName || first.uploadedBy || '-', first.uploadedByRoleId);
            const sizeText = typeof first.fileSizeKB === 'number' ? `${first.fileSizeKB} KB` : '-';
            const pagesText = typeof first.numberOfPages === 'number' ? first.numberOfPages : '-';
            
            return { 
                first, 
                fileName, 
                docTypeNames, 
                allDocuments: docs,
                uploadedAt, 
                uploadedBy, 
                sizeText, 
                pagesText,
                isReverted: false
            };
        }).sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

        // Process reverted documents (latest first)
        const revertedRows = Array.from(revertGroups.values()).map((docs, idx) => {
            const first = docs[0] as any;
            const fileName = Array.isArray(first.documentName) ? (first.documentName?.[0] || '') : first.documentName;
            const docTypeNames = Array.from(new Set(
                docs.map((d) => getDocTypeName(d.documentTypeId) || (d as any).name).filter(Boolean)
            )).join(', ');
            const uploadedAt = first.dateUploadedAt ? moment.utc(first.dateUploadedAt).local().format('MM/DD/YYYY HH:mm') : '-';
            // Use the uploadedByFullName field from the API response, fallback to current user if not available
            const uploadedBy = getDisplayName(first.uploadedByFullName || first.uploadedBy || '-', first.uploadedByRoleId);
            // Use the fileSizeKB field from the API response (it's already in KB)
            const sizeText = typeof first.fileSizeKB === 'number' ? `${first.fileSizeKB} KB` : '-';
            const pagesText = typeof first.numberOfPages === 'number' ? first.numberOfPages : '-';
            
            return { 
                first, 
                fileName, 
                docTypeNames, 
                allDocuments: docs, // Store all documents for deletion
                uploadedAt, 
                uploadedBy, 
                sizeText, 
                pagesText,
                isReverted: true
            };
        }).sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());

        // Return uploaded documents first, then reverted documents
        return [...uploadedRows, ...revertedRows];
    }, [UploadedDocument, revertedDocument, requiredDocument, currentUser]);

    function UploadSection() {
        return (
        <div ref={uploadAreaRef}>
            <React.Fragment>
                <Formik
                    initialValues={initialValues}
                    validateOnChange={true}
                    onSubmit={(data, { setSubmitting }) => {
                        if (!selectedFiles) {
                            Swal.fire("Error", "Please select at least one document.", "error");
                            setSubmitting(false);
                            return;
                        }

                        const formData = new FormData();
                        formData.append('document', selectedFiles);
                        formData.append("CustomerPolicyId", customerPolicyId);
                        formData.append("data", JSON.stringify(data));
                        
                        // Add current user ID to the FormData
                        if (currentUser) {
                            // Use the userId property from authData interface
                            formData.append("currentUserId", currentUser?.userId?.toString() || '');
                        }
                        
                        docTypeIds.forEach((id) => {
                            formData.append("docTypeIds[]", id.toString());
                        });

                        setLoading(true);
                        apiClient(`/customer/UploadDocuments/`, {
                            method: "POST",
                            headers: new Headers({
                                Authorization: `Bearer ${token}`,
                            }),
                            body: formData,
                        })
                            .then((res) => {
                                if (!res) return;
                                Swal.fire({
                                    title: 'Success!',
                                    text: 'The documents have been successfully uploaded.',
                                    icon: 'success',
                                    confirmButtonColor: '#3085d6',
                                    confirmButtonText: 'Ok'
                                });
                            })
                            .finally(() => {
                                setSubmitting(false);
                                setLoading(false);
                                setSelectedFiles(null);
                                setRequiredDocument([]);
                                setSelectedDocTypeIds([]);
                                getUploadedDocuments();
                                getRequiredDocuments();
                            });
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <DialogContent>
                                {requiredDocument.length > 0 &&
                                    <>
                                        <div className="card-body">
                                            <div className="document-item">
                                                <GenericStyledDropZone
                                                    handleFileDrop={handleFileDrop}
                                                    limitSize={policyDetail?.homeState === 'NY' ? 1 : 10}
                                                    homeState={policyDetail?.homeState}
                                                    isClientUser={isClientUser()}
                                                />
                                                { selectedFiles &&
                                                        <div className="uploaded-files" style={{ marginTop: '10px' }}>
                                                            <div className="file-item" style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                border: '1px solid #e0e0e0',
                                                                borderRadius: '6px',
                                                                background: '#f7f7f7',
                                                                padding: '8px 12px'
                                                            }}>
                                                                <span className="file-name" style={{ color: '#162E3C' }}>
                                                                {selectedFiles?.name}
                                                            </span>
                                                                <IconButton size="small" color="inherit" onClick={() => handleFileRemove()}>
                                                                    <CancelIcon fontSize="small" />
                                                                </IconButton>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                        <List
                                        sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '12px',
                                        }}
                                        >
                                        {selectedFiles &&
                                            requiredDocument.map((item) => (
                                            <ListItem
                                                key={item.documentTypeId}
                                                onClick={() => handleCheckboxChange(item)}
                                                sx={{
                                                    flex: '0 1 calc(50% - 8px)', // 2 items per row
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '6px 8px',
                                                    borderBottom: '1px dashed #e0e0e0',
                                                    backgroundColor: '#fff',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Checkbox
                                                checked={docTypeIds.includes(item.documentTypeId)}
                                                sx={{
                                                        padding: '4px',
                                                    pointerEvents: "none",
                                                }}
                                                />
                                                <ListItemText
                                                sx={{
                                                        fontSize: '14px',
                                                        color: '#162E3C',
                                                        fontWeight: '500',
                                                }}
                                                >
                                                {item.documentName}
                                                {item.isRequired && (
                                                    <span
                                                    style={{
                                                        color: "red",
                                                            marginLeft: '4px',
                                                            fontSize: '16px',
                                                            fontWeight: '600',
                                                    }}
                                                    >
                                                    *
                                                    </span>
                                                )}
                                                </ListItemText>
                                            </ListItem>
                                            ))}
                                        </List>
                                    </>
                                }
                                <div className="card-body">
                                    <div className="button-container">
                                        {requiredDocument.length > 0 &&
                                            <button
                                                type="submit"
                                                className={`active-btn-blue ${loading ? 'btn-disabled' : ''}`}
                                                disabled={loading || docTypeIds.length == 0}
                                            >
                                                Upload
                                            </button>
                                        }
                                        <button
                                            className={`active-btn-blue`}
                                            type="button"
                                            onClick={SentToMisc}
                                        >
                                            Send to MISC
                                        </button>
                                        <button
                                            className="active-btn-white"
                                            type="button"
                                            onClick={handleBoxClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </React.Fragment>
        </div>
        );
    }
    
    return (
                <Dialog
                    open={open}
                    onClose={handleBoxClose}
                    fullWidth
                    maxWidth={false} // Disable default max width
                    sx={{
                        "& .MuiDialog-paper": {
                            width: "70%", // Set a fixed width for better alignment
                            maxWidth: "100%", // Ensure responsiveness
                            margin: "0 auto", // Center horizontally
                            borderRadius: "8px", // Add rounded corners
                            padding: "20px", // Add padding for spacing
                            textAlign: "left"
                        },
                    }}>
                       
                        <Box sx={{ backgroundColor: '#FFFFFF' }}>
                        <DialogTitle 
                            className="card-title" 
                            sx={{ 
                                textAlign: 'center',
                                fontWeight: 700,
                                fontSize: '28px'
                            }}>
                            Transaction Documents <br/>
                            <span style={{ fontSize: '16px', color: '#162E3C', fontWeight: '500' }}>
                                Policy Number: {policyDetail?.policyNumber}
                            </span>
                        </DialogTitle>
                        {/* Uploaded Documents Table*/}
                        <div className="card-body">
                            {loading && <LinearProgress sx={{ backgroundColor: "#132D3D" }} />}
                            {groupedRows && groupedRows.length > 0 ? (
                                <Box sx={{ width: '100%', mb: 2 }}>
                                    <DataGridPro
                                            autoHeight
                                            getRowHeight={() => 'auto'}
                                            rows={groupedRows.map((r, idx) => ({
                                                id: `${r.first.customerPolicyDocumentId}-${idx}`,
                                                fileName: r.fileName,
                                                docTypeNames: r.docTypeNames,
                                                uploadedAt: r.uploadedAt,
                                                uploadedBy: r.uploadedBy,
                                                sizeText: r.sizeText,
                                                pagesText: r.pagesText,
                                                first: r.first,
                                                allDocuments: r.allDocuments,
                                                isReverted: r.isReverted
                                            }))}
                                            columns={([
                                                { field: 'fileName', headerName: 'File Name', flex: 1, minWidth: 200, renderCell: (params) => (
                                                    <Link href={params.row.first.blob_Address} target="_blank" sx={{ textDecoration: 'none', overflow: 'visible', whiteSpace: 'normal',wordBreak: 'break-word',lineHeight: '1',display: 'block',padding: '2px 0'}}>{params.value || '-'}</Link>
                                                ) },
                                                { field: 'docTypeNames', headerName: 'Document Types', flex: 1, minWidth: 310, maxWidth: 400, renderCell: (params) => (
                                                    <Box sx={{ width: '100%', verflow: 'visible', whiteSpace: 'normal',wordBreak: 'break-word',lineHeight: '1',padding: '4px 0'}}>
                                                        {params.value || '-'}
                                                    </Box>
                                                ) },
                                                { field: 'uploadedAt', headerName: 'Upload Date', flex: 0.8, minWidth: 160 },
                                                { field: 'uploadedBy', headerName: 'Uploaded By', flex: 0.8, minWidth: 160 },
                                                { field: 'sizeText', headerName: 'File Size', flex: 0.6, minWidth: 120 },
                                                // { field: 'pagesText', headerName: 'Pages', flex: 0.4, minWidth: 100 },
                                                ...(canDeleteDocuments() ? [{
                                                    field: 'actions', 
                                                    headerName: 'Actions', 
                                                    sortable: false, 
                                                    filterable: false, 
                                                    flex: 0.5, 
                                                    minWidth: 120, 
                                                    renderCell: (params: any) => (
                                                        // Only show delete button for non-reverted documents
                                                        !params.row.isReverted ? (
                                                            <IconButton color="error" onClick={() => {
                                                                handleRemoveUploadedDoc(params.row)
                                                            }}>
                                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M3 6H5M5 6H21M5 6V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V6H5ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="#162E3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            </IconButton>
                                                        ) : (
                                                            <IconButton color="error" onClick={() => {
                                                                handleRemoveUploadedDoc(params.row)
                                                            }}>
                                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M3 6H5M5 6H21M5 6V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V6H5ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="#162E3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            </IconButton>
                                                        )
                                                    )
                                                }] : [])
                                            ]) as GridColDef[]}
                                            pagination
                                            rowsPerPageOptions={[5, 10, 25]}
                                            pageSize={rowsPerPage}
                                            onPageSizeChange={(newPageSize: number) => setRowsPerPage(newPageSize)}
                                            onPageChange={(newPage: number) => setPage(newPage)}
                                            disableSelectionOnClick
                                            disableColumnMenu
                                            sx={{
                                                borderRadius: '10px',
                                                overflow: 'hidden',
                                                fontFamily: "'Proxima Nova', sans-serif",
                                                fontSize: '14px',
                                                '& .MuiDataGrid-main': {
                                                    overflow: 'hidden'
                                                },
                                                '& .MuiDataGrid-cell': {
                                                    overflow: 'visible',
                                                    whiteSpace: 'normal',
                                                    alignItems: 'flex-start',
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                },
                                                '& .MuiDataGrid-row': {
                                                    minHeight: '52px !important',
                                                }
                                            }}
                                            getRowClassName={(params) => {
                                                return params.row.isReverted ? 'reverted-row' : '';
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ 
                                        width: '100%', 
                                        textAlign: 'center', 
                                        py: 4, 
                                        color: '#666',
                                        fontFamily: "'Proxima Nova', sans-serif",
                                        fontSize: '16px'
                                    }}>
                                        No documents available
                                    </Box>
                                )}
                            </div>    
                             {/* Upload Section - hidden in viewOnly mode */}
                            {!viewOnly && UploadSection()}
                            </Box>
                </Dialog>
            );
        };

// NY File Size Popup Component
const NYFileSizePopup: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{
                "& .MuiDialog-paper": {
                    borderRadius: "8px",
                    padding: "20px",
                },
            }}
        >
            <DialogTitle sx={{ 
                fontWeight: 600, 
                fontSize: '20px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e0e0e0',
                marginBottom: '16px'
            }}>
                Notice: File Upload Limit in New York
            </DialogTitle>
            <DialogContent>
                <Box sx={{ textAlign: 'left' }}>
                    <Box sx={{ marginBottom: '16px' }}>
                        <Box sx={{ 
                            lineHeight: 1.6, 
                            marginBottom: '16px',
                            color: '#162E3C'
                        }}>
                            Please be advised that a file size limit of{' '}
                            <Box component="span" sx={{ fontWeight: 700 }}>1MB</Box>
                            {' '}is required for the New York{' '}
                            <Box component="span" sx={{ fontWeight: 700 }}>ELANY Filing System</Box>
                            . Files exceeding this limit will not be accepted by the system.
                        </Box>
                        <Box sx={{ 
                            lineHeight: 1.6, 
                            marginBottom: '16px',
                            color: '#162E3C'
                        }}>
                            We appreciate your understanding and cooperation.
                        </Box>
                    </Box>
                    
                    <Box sx={{ 
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <Box component="span" sx={{ lineHeight: 1.6, color: '#162E3C', fontWeight: 700 }}>
                            Helpful Tips
                        </Box>
                        <Tooltip
                            title={
                                <Box sx={{ padding: '8px' }}>
                                    <Box sx={{ fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>
                                        Helpful Tips
                                    </Box>
                                    <Box component="ol" sx={{ margin: 0, paddingLeft: '20px', lineHeight: 1.8, fontSize: '13px' }}>
                                        <li>Save as a reduced-size PDF</li>
                                        <li>Send Part C, NELP, and Dec Page separately if trying to upload all as one file</li>
                                        <li>When uploading a policy*, only upload the declarations page. Please include policy limits, carrier participation, and risk locations if located on separate pages.</li>
                                    </Box>
                                    <Box sx={{ 
                                        lineHeight: 1.6, 
                                        fontSize: '13px', 
                                        color: '#F2F2F2',
                                        marginBottom: '16px'
                                     }}>
                                        <Box component="span" sx={{ fontWeight: 700 }}>*Note:</Box> ELANY also allows new and renewal business to be filed on a binder; however, the declarations page MUST be submitted to ELANY for stamping once the policy has been issued.
                                    </Box>
                                </Box>
                            }
                            arrow
                            placement="right"
                        >
                            <IconButton
                                size="small"
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: '#2196f3',
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    padding: 0,
                                    '&:hover': {
                                        backgroundColor: '#1976d2',
                                    }
                                }}
                            >
                                <Box component="span" sx={{ fontSize: '12px', lineHeight: 1 }}>i</Box>
                            </IconButton>
                        </Tooltip>
                    </Box>                
                    
                    <Box sx={{ 
                        lineHeight: 1.6, 
                        fontSize: '14px',
                        color: '#2196f3'
                    }}>
                        Please refer to the State Information Portal for further details regarding required filing documents.
                    </Box>
                </Box>
            </DialogContent>
            <Box sx={{ 
                padding: '16px 24px',
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e0e0e0'
            }}>
                <button
                    onClick={onClose}
                    className="active-btn-blue"
                    style={{ minWidth: '80px' }}
                >
                    OK
                </button>
            </Box>
        </Dialog>
    );
};

function GenericStyledDropZone(props: any & RequiredDocument) {
    const { handleFileDrop, limitSize = 10, item, homeState, isClientUser = false } = props;
    const [nyPopupOpen, setNyPopupOpen] = useState(false);

    const showNYFileSizePopup = () => {
        setNyPopupOpen(true);
    };

    const onDropRejected = (fileRejections: FileRejection[]) => {
        const first = fileRejections?.[0];
        const errCodes = first?.errors?.map((e) => e.code) || [];

        if (errCodes.includes('file-too-large')) {
            if (homeState === 'NY' && isClientUser) {
                showNYFileSizePopup();
                return;
            }
            Swal.fire({
                icon: 'error',
                title: 'File too large',
                text: `Maximum file size ${limitSize}MB.`,
                confirmButtonText: 'OK'
            });
            return;
        }
        if (errCodes.includes("file-invalid-type")) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid file type',
                text: `Only .pdf, Word, and image files are allowed.`,
                confirmButtonText: 'OK'
            });
            return;
        }
        Swal.fire({
            icon: 'error',
            title: 'Upload failed',
            text: `Please check your file and try again.`,
            confirmButtonText: 'OK'
        });
        return;
    }

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        maxFiles: 10,
        maxSize: limitSize * 1024 * 1024,
        accept: {
            "application/pdf": [".pdf"],
            "image/*": [],
            "application/msg": [".msg"],
            "application/word": [".word"],
        },
        onDrop: (files) => {
            const file = files?.[0];
            if (!file) return;

            const newFileName = `${files[0].name}`;
            const modifiedFile = new File([files[0]], newFileName, {
                type: files[0].type,
                lastModified: files[0].lastModified,
            });

            handleFileDrop(modifiedFile);
        },
        onDropRejected,
    });

    const focusedStyle = {
        borderColor: "#2196f3",
    };

    const acceptStyle = {
        borderColor: "#00e676",
    };

    const rejectStyle = {
        borderColor: "#ff1744",
    };

    const baseStyle = {
        flex: 1,
        display: "flex",
        alignItems: "center",
        padding: "10px",
        borderWidth: 1,
        borderRadius: 8,
        borderColor: "#162E3C80",
        borderStyle: "dashed",
        backgroundColor: "rgba(22, 46, 60, 0.10)",
        color: "#162E3C",
        outline: "none",
        transition: "border .24s ease-in-out",
    };

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    return (
        <>
            <NYFileSizePopup open={nyPopupOpen} onClose={() => setNyPopupOpen(false)} />
            <div className="box-style col-lg-12">
                <div {...getRootProps({ style })}>
                    <input {...getInputProps()} name={"diligentdocument"} multiple />
                    <p
                        style={{
                            fontSize: "16px",
                            color: "#162E3C",
                            fontWeight: "600",
                        }}
                    >
                        Select File...
                    </p>
                </div>
            </div>
        </>
    );
}
